import React, { memo, useState, useMemo, useCallback, useEffect } from 'react';
import { slice, findLastIndex, last } from 'ramda';
import moment from 'moment';
import classNames from 'classnames';
import { Typography, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { LinkButton } from '@alycecom/ui';

import { SLOT_TIME_FORMAT_SMALL } from '../../../../../constants/dates.constants';
import { ISlot } from '../../../store/meeting/meeting.types';

import Slot from './Slot';
import DaySkeleton from './DaySkeleton';

const MIN_VISIBLE_ITEMS = 4;

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  mobileEmptySlots: {
    [breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  noTimes: {
    color: palette.text.primary,
  },
}));

export interface IDayProps {
  day: string;
  slots?: ISlot[];
  selectedSlots?: ISlot[];
  isLoading?: boolean;
  timezoneName: string;
  slotsTimeFormat?: string;
  onSelect: (slot: ISlot, isSelected: boolean) => void;
}

const Day = ({
  day,
  slots = [],
  timezoneName,
  slotsTimeFormat = SLOT_TIME_FORMAT_SMALL,
  isLoading = false,
  selectedSlots = [],
  onSelect,
}: IDayProps) => {
  const classes = useStyles();

  const [canShowMore, setCanShowMore] = useState(true);
  const hasNoSlots = !isLoading && slots.length === 0;

  const date = moment(day);
  const dayName = date.format('ddd');
  const fullDate = date.format('YYYY-MM-DD');
  const monthAndDay = date.format('MMM DD');
  const dayOfWeek = date.format('E');

  const isLastSelectedSlotInvisible = useMemo(() => {
    if (selectedSlots.length > 0) {
      const { startAt, endAt } = last(selectedSlots) as ISlot;
      const lastSelectedIndex = findLastIndex(slot => slot.startAt === startAt && slot.endAt === endAt, slots);
      return lastSelectedIndex >= MIN_VISIBLE_ITEMS;
    }
    return false;
  }, [slots, selectedSlots]);

  const visibleSlots = useMemo(
    () => (canShowMore && !isLastSelectedSlotInvisible ? slice(0, MIN_VISIBLE_ITEMS, slots) : slots),
    [canShowMore, isLastSelectedSlotInvisible, slots],
  );

  const isShowMoreLinkVisible = !isLoading && canShowMore && visibleSlots.length < slots.length;
  const moreSlotsCount = slots.length - MIN_VISIBLE_ITEMS;

  const handleShowMoreSlots = useCallback(() => setCanShowMore(false), []);

  const checkIsSlotSelected = useCallback(
    ({ startAt, endAt }) => selectedSlots.findIndex(slot => slot.startAt === startAt && slot.endAt === endAt) !== -1,
    [selectedSlots],
  );

  useEffect(() => {
    if (isLastSelectedSlotInvisible) {
      setCanShowMore(false);
    }
  }, [isLastSelectedSlotInvisible]);

  return (
    <Box
      data-testid={`Calendar.DayWrapper.${dayOfWeek}`}
      className={classNames({ [classes.mobileEmptySlots]: hasNoSlots })}
      width={['100%', 174]}
      minWidth={['auto', 174]}
      mx={[0, 3]}
      px={[3, 0]}
    >
      <Box width={1} mt={3} mb={2}>
        <Typography data-testid={`Calendar.DayTitle.${dayOfWeek}`} className="H4-Dark-Bold">
          <Box component="span" display={['inline-block', '']} width={[50, 'auto']} color="text.primary">
            {dayName}
          </Box>
          &nbsp;
          <Box component="span" color="grey.main" fontWeight="bold">
            {monthAndDay}
          </Box>
        </Typography>
      </Box>
      {hasNoSlots && (
        <Box width={1}>
          <Typography
            data-testid={`Calendar.DayEmptySlots.${dayOfWeek}`}
            className={classNames('Body-Regular-Left-Static', classes.noTimes)}
          >
            No times available
          </Typography>
        </Box>
      )}
      <Box
        data-testid={`Calendar.SlotsWrapper.${dayOfWeek}`}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="center"
      >
        {isLoading ? (
          <DaySkeleton />
        ) : (
          visibleSlots.map(slot => (
            <Slot
              key={`${slot.startAt}-${slot.endAt}`}
              startAt={slot.startAt}
              endAt={slot.endAt}
              fullDate={fullDate}
              timezoneName={timezoneName}
              format={slotsTimeFormat}
              isSelected={checkIsSlotSelected(slot)}
              onSelect={isSelected => onSelect(slot, isSelected)}
            />
          ))
        )}
      </Box>
      {isShowMoreLinkVisible && (
        <Box textAlign="center" fontSize={14} fontFamily="Lato">
          <LinkButton data-testid={`Calendar.Day.SeeMore.Button.${dayOfWeek}`} onClick={handleShowMoreSlots}>
            See ({moreSlotsCount}) more
          </LinkButton>
        </Box>
      )}
    </Box>
  );
};

export default memo(Day);
